import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { getThemeColor } from '../../config/appConfig';
import { performLogout } from '../../utils/logout';

const THEME = getThemeColor('primary');

const UserAccountDropdown = ({ userName, userEmail, userInitial, userHandle }) => {
  const navigate = useNavigate();
  const { clearUserData } = useUser();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const handle =
    userHandle ||
    (userEmail?.includes('@') ? `@${userEmail.split('@')[0]}` : userEmail ? `@${userEmail}` : '');

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const goProfile = () => {
    setOpen(false);
    navigate('/profile');
  };

  const handleLogout = () => {
    setOpen(false);
    performLogout(clearUserData);
    navigate('/login');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow transition"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account menu"
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
          style={{ backgroundColor: THEME }}
        >
          {userInitial}
        </div>
        <div className="hidden sm:block text-left min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight truncate max-w-[140px]">
            {userName}
          </p>
          {handle ? (
            <p className="text-[11px] text-gray-400 truncate max-w-[140px]">{handle}</p>
          ) : null}
        </div>
      </button>

      {open ? (
        <div
          className="absolute right-0 mt-2 w-48 bg-white rounded-xl border border-gray-200 shadow-lg z-[60] py-1 overflow-hidden"
          role="menu"
        >
          <div className="px-4 py-3 border-b border-gray-100 sm:hidden">
            <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
            {handle ? <p className="text-xs text-gray-400 truncate">{handle}</p> : null}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={goProfile}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition text-left"
          >
            <User className="w-4 h-4 text-gray-500 shrink-0" />
            Profile
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition text-left"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Logout
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default UserAccountDropdown;
